/**
 * FormSection - Bloco de seção de formulário no padrão "premium" do sistema
 *
 * Extraído do layout usado no modal de criação de negócios (DealFormDialog):
 * ícone + título + (opcional) sufixo, seguido do conteúdo da seção.
 * Serve para padronizar modais de formulário e evitar duplicação de estilos.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (padrão DealFormDialog)
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** Props do cabeçalho/seção */
interface FormSectionProps {
  /** Ícone lucide exibido à esquerda do título */
  icon: LucideIcon;
  /** Título curto da seção */
  title: string;
  /** Texto auxiliar exibido após o título (ex.: "(opcional)") */
  hint?: string;
  /** Conteúdo exibido à direita do cabeçalho (ex.: badges/ações) */
  actions?: ReactNode;
  /** Campos da seção */
  children: ReactNode;
  className?: string;
}

/** Seção de formulário com cabeçalho padronizado */
export function FormSection({
  icon: Icon,
  title,
  hint,
  actions,
  children,
  className,
}: FormSectionProps) {
  return (
    <section className={className}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
        </div>
        {actions}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Props do micro-label padronizado */
interface FieldLabelProps {
  /** Vincula o label ao input (htmlFor) */
  htmlFor?: string;
  /** Marca o campo como obrigatório (asterisco) */
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/** Micro-label maiúsculo usado nos formulários premium */
export function FieldLabel({ htmlFor, required, children, className }: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
        className,
      )}
    >
      {children}
      {required && <span className="text-destructive">*</span>}
    </label>
  );
}
